import React, { useState } from 'react';
import { Box, Menu, MenuItem, Typography } from '@material-ui/core';
import { useScope } from '@/hooks/useScope';
import TroubleshootingDialog from '@/components/pages/kiosk/tablet/Troubleshooting/TroubleshootingDialog';
import TroubleshootingSnackbar from '@/components/pages/kiosk/tablet/Troubleshooting/TroubleshootingSnackbar';
import { TroubleshootingAlertProps } from '@/components/pages/kiosk/tablet/Troubleshooting/types';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListItem from '@material-ui/core/ListItem';
import { ContactSupportRounded, HelpOutlined, Refresh } from '@material-ui/icons';
import { gql, useMutation } from '@apollo/client';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';

const refreshScreen = () => {
  window.location.reload();
};

const issueListOptions = [
  {
    label: 'TV or iPad not working or showing wrong info',
    value: 'tv_or_ipad_not_working_or_showing_wrong_info',
  },
  {
    label: 'Patient is missing, in wrong place, or has incorrect status',
    value: 'patient_is_missing__in_wrong_place__or_has_incorrect_status',
  },
  {
    label: 'Caretaker message not received or contact info is wrong',
    value: 'caretaker_message_not_received_or_contact_info_is_wrong',
  },
  {
    label: 'Can’t complete a task (assign room, mark ready, send family message, etc.)',
    value: 'can’t_complete_a_task__assign_room__mark_ready__send_family_message__etc._',
  },
];

const Troubleshooting = ({ roomName, variant = 'text' }: { roomName?: string; variant?: 'text' | 'listItem' }) => {
  const classes = useStyles();
  const scope: any = useScope();
  const hospitalName = scope?.hospital?.name;

  const [openDialog, setOpenDialog] = useState(false);
  const [issue, setIssue] = useState<string>('');
  const [otherIssue, setOtherIssue] = useState('');
  const [sending, setSending] = useState(false);
  const [alert, setAlert] = useState<TroubleshootingAlertProps>({ open: false });
  const [issueList] = useState(issueListOptions); // Initialize issueList as state
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleIssueChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    setIssue(event.target.value as string);
  };

  const handleClickOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const clearForm = () => {
    setIssue('');
    setOtherIssue('');
  };

  const handleCloseSnackbar = (event: any) => {
    setAlert({ ...alert, open: false });
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [createSupportTicket] = useMutation(
    gql`
      mutation createSupportTicket($subject: String!, $comment: String!) {
        createSupportTicket(subject: $subject, comment: $comment)
      }
    `,
    {
      variables: {
        subject: '',
        comment: '',
      },
    }
  );

  const handleSubmit = async event => {
    event.preventDefault();
    setSending(true);
    try {
      const result = await createSupportTicket({
        variables: {
          subject: `Support Request • ${hospitalName} • ${roomName || 'Unknown Room'} • ${
            issueList.find(item => item.value === issue)?.label || 'Other'
          } ${otherIssue ? ' •' + otherIssue : ''}`,
          comment: otherIssue,
        },
      });

      if (result.data.createSupportTicket) {
        setAlert({ open: true, severity: 'success', message: 'Request submitted successfully!' });
        handleCloseDialog();
        clearForm();
      } else {
        setAlert({ open: true, severity: 'error', message: 'Unable to submit request at this time' });
      }
    } catch (error: any) {
      if (error?.response) {
        setAlert({ open: true, severity: 'error', message: error?.response.data.description });
        console.error('Error response:', error.response.data);
      } else if (error?.request) {
        setAlert({ open: true, severity: 'error', message: error?.request.message });
        console.error('Error request:', error.request);
      } else {
        setAlert({ open: true, severity: 'error', message: error?.message });
        console.error('Error message:', error.message);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {variant === 'text' ? (
        <Box display="flex" justifyContent="center" alignItems="center" className={classes.root}>
          <Typography color="textSecondary">Experiencing problems with the app?</Typography>
          <Button onClick={refreshScreen} startIcon={<Refresh />}>
            Click to refresh
          </Button>
          <Button variant="outlined" onClick={handleClickOpenDialog} startIcon={<ContactSupportRounded />}>
            Click here for support
          </Button>
        </Box>
      ) : (
        <>
          <ListItem button onClick={handleClick}>
            <ListItemIcon>
              <HelpOutlined />
            </ListItemIcon>
            <ListItemText primary="Having trouble?" />
          </ListItem>

          <Menu
            id="troubleshooting-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
          >
            <MenuItem onClick={refreshScreen}>Refresh Screen</MenuItem>
            <MenuItem onClick={handleClickOpenDialog}>Support request</MenuItem>
          </Menu>
        </>
      )}

      <TroubleshootingDialog
        openDialog={openDialog}
        handleCloseDialog={handleCloseDialog}
        handleSubmit={handleSubmit}
        handleIssueChange={handleIssueChange}
        issueList={issueList}
        issue={issue}
        setOtherIssue={setOtherIssue}
        sending={sending}
      />

      <TroubleshootingSnackbar alert={alert} handleCloseSnackbar={handleCloseSnackbar} />
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

export default Troubleshooting;
